import role0Schema from '../../../../assets/forms/role-0.form.json';
import typesDict from '../../../../assets/types.dict.json';
import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import {
  buildWarningBag,
  sheetHasTriggeredWarning,
  WarnableControl,
} from './warning-eval.helper';
import {
  checkCurrentEndFloor,
  checkCurrentRange,
  checkDateSequence,
  checkPreviousRange,
  deriveDisabledKeys,
  deriveStaticRequiredKeys,
  DisableDerivableControl,
  FILING_INFO_CONDITIONAL_RULES,
  FILING_INFO_FIELD_KEYS,
  RequiredDerivableControl,
} from './filing-info-common.rules';
import { SHEET_KEYS } from '../../../models/sheet-keys';

/**
 * Pure validator for Role 0 (Filing Prerequisites).
 *
 * Mirrors the editor's `setupConditionalValidation()` /
 * `setupFinancialYearDateValidation()` plus the static `Validators.required`
 * + pattern validators applied in `buildForm()` of
 * role0-000000-filing-prerequisites.component.ts. The component is the
 * source of truth — when it changes, this validator must change too.
 *
 * Why these rules are duplicated rather than imported from the component:
 *   The component's rules are spread across Angular FormControl validators
 *   and rxjs valueChanges subscriptions that mutate FormGroup state. They
 *   only run when the component is mounted. Pure validators have to compute
 *   the same answer from a flat sheetData snapshot with no FormGroup, so
 *   the dependency graph is re-encoded here in pure form.
 *
 * Parity coverage:
 *   - Static required fields  (required === true OR an unconditional
 *     validators:[{name:'required'}] entry — derived via the shared
 *     deriveStaticRequiredKeys so role0 and role1 agree; previously role0
 *     only honoured the top-level flag and silently diverged from role1)
 *   - Pattern validators      (schema.controls[].validators[name=='pattern'])
 *   - 5 conditional required rules (see CONDITIONAL_RULES below)
 *   - Cross-field date validation (date range + date sequence)
 *   - Disabled-field exclusion (`ui.readonly` or a matching `ui.disabledWhen`):
 *     a disabled control fires no validators in Angular, so neither required nor
 *     pattern may flag it here. See deriveDisabledKeys in
 *     filing-info-common.rules.ts for why this is load-bearing for
 *     ssmt-dei:TypeOfSubmission.
 */

const STATIC_REQUIRED_KEYS: ReadonlySet<string> = deriveStaticRequiredKeys(
  role0Schema.controls as ReadonlyArray<RequiredDerivableControl>
);

/**
 * The same control list viewed through the disable lens. Kept as a module
 * constant so `deriveDisabledKeys` only re-evaluates the `disabledWhen`
 * predicates (which depend on sheetData) per call, not the cast.
 */
const DISABLE_DERIVABLE_CONTROLS =
  role0Schema.controls as unknown as ReadonlyArray<DisableDerivableControl>;

const PATTERN_BY_KEY: ReadonlyMap<string, RegExp> = (() => {
  const map = new Map<string, RegExp>();
  const patterns = (typesDict as { patterns?: Record<string, string> }).patterns ?? {};
  for (const control of role0Schema.controls as Array<{
    qname: string;
    validators?: Array<{ name?: string; patternSource?: string }>;
  }>) {
    const patternValidator = control.validators?.find((validator) => validator.name === 'pattern');
    if (patternValidator?.patternSource) {
      const patternKey = patternValidator.patternSource.replace('type:', '');
      const pattern = patterns[patternKey];
      if (pattern) {
        try {
          map.set(control.qname, new RegExp(pattern));
        } catch {
          // Invalid regex source in types dict — skip silently; component
          // would also fail to apply this validator, so parity holds.
        }
      }
    }
  }
  return map;
})();

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function effectiveRequiredKeys(sheetData: Record<string, any>): Set<string> {
  const keys = new Set<string>(STATIC_REQUIRED_KEYS);
  for (const rule of FILING_INFO_CONDITIONAL_RULES) {
    if (rule.whenAnyOf.includes(sheetData[rule.trigger])) {
      for (const field of rule.requireFields) keys.add(field);
    }
  }
  // A disabled control fires no validators in the editor — see
  // deriveDisabledKeys in filing-info-common.rules.ts.
  for (const key of deriveDisabledKeys(DISABLE_DERIVABLE_CONTROLS, sheetData)) {
    keys.delete(key);
  }
  return keys;
}

function checkRequiredErrors(sheetData: Record<string, any>): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const key of effectiveRequiredKeys(sheetData)) {
    if (isEmpty(sheetData[key])) {
      errors[key] = `${key} is required`;
    }
  }
  return errors;
}

function checkPatternErrors(sheetData: Record<string, any>): Record<string, string> {
  const errors: Record<string, string> = {};
  const disabled = deriveDisabledKeys(DISABLE_DERIVABLE_CONTROLS, sheetData);
  for (const [key, pattern] of PATTERN_BY_KEY) {
    if (disabled.has(key)) continue;
    const value = sheetData[key];
    if (typeof value === 'string' && value.length > 0 && !pattern.test(value)) {
      errors[key] = `${key} has invalid format`;
    }
  }
  return errors;
}

/**
 * Cross-field date validation. Delegates to the shared check* primitives
 * in filing-info-common.rules.ts so the editor's Angular validator
 * functions and this pure validator emit identical messages.
 */
function checkDateErrors(sheetData: Record<string, any>): Record<string, string> {
  const errors: Record<string, string> = {};
  const currentRange = checkCurrentRange(
    sheetData[FILING_INFO_FIELD_KEYS.currentYearStart],
    sheetData[FILING_INFO_FIELD_KEYS.currentYearEnd]
  );
  if (currentRange) errors[FILING_INFO_FIELD_KEYS.currentYearStart] = currentRange;

  const previousRange = checkPreviousRange(
    sheetData[FILING_INFO_FIELD_KEYS.previousYearStart],
    sheetData[FILING_INFO_FIELD_KEYS.previousYearEnd]
  );
  if (previousRange) errors[FILING_INFO_FIELD_KEYS.previousYearStart] = previousRange;

  const sequence = checkDateSequence(
    sheetData[FILING_INFO_FIELD_KEYS.previousYearEnd],
    sheetData[FILING_INFO_FIELD_KEYS.currentYearStart]
  );
  if (sequence) errors[FILING_INFO_FIELD_KEYS.previousYearEnd] = sequence;

  // Unsupported filing period: a current financial year END before
  // 2017-01-31 belongs to the "Financial Statements 1965" submission. Keyed
  // onto the END date itself (unlike the cross-field rules above, which key
  // onto the earlier of the pair) because it is a single-field constraint —
  // matching where the editor's `financialYearEndUnsupported` error lands.
  const currentEndFloor = checkCurrentEndFloor(
    sheetData[FILING_INFO_FIELD_KEYS.currentYearEnd]
  );
  if (currentEndFloor) errors[FILING_INFO_FIELD_KEYS.currentYearEnd] = currentEndFloor;

  return errors;
}

export const role0FilingPrerequisitesValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.filingPrerequisites,
  // Tier 1 by primary characteristic (required-field driven), even though it
  // also encodes a small handful of conditional rules and date constraints.
  // Stays in Tier 1 so the parity harness does not gate it on Tier 4 rules.
  tier: 1,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    const errors: Record<string, string> = {
      ...checkRequiredErrors(sheetData),
      ...checkPatternErrors(sheetData),
      ...checkDateErrors(sheetData),
    };
    return {
      hasErrors: Object.keys(errors).length > 0,
      hasSuggestedChanges: sheetHasTriggeredWarning(
        role0Schema.controls as unknown as WarnableControl[],
        sheetData,
        buildWarningBag(
          context.crossSheetData?.get(SHEET_KEYS.filingInfo) ??
            context.crossSheetData?.get(SHEET_KEYS.filingPrerequisites) ??
            {},
          sheetData
        )
      ),
      errors,
    };
  },
};
